import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Select, Typography, Tag, Space, Button, Badge, Empty, Statistic,
  Alert, Divider,
} from 'antd';
import { SignalFilled, ReloadOutlined, EnvironmentOutlined, ApiOutlined } from '@ant-design/icons';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Cell, LabelList } from 'recharts';
import { getEvents, getRssiDistribution, getReaders } from '../api';

const { Title, Text } = Typography;
const { Option } = Select;

const PRIMARY = '#0F1D6B';
const TEAL    = '#00838F';

const QUALITY = {
  excellent: { color: '#10b981', label: 'EXCELLENT', tip: 'Avg RSSI ≥ −55 dBm — ideal range, close & clean reads' },
  good:      { color: '#22d3ee', label: 'GOOD',      tip: 'Avg RSSI between −65 and −55 dBm — typical strong reads' },
  fair:      { color: '#f59e0b', label: 'FAIR',      tip: 'Avg RSSI between −75 and −65 dBm — adequate but check coverage' },
  poor:      { color: '#ef4444', label: 'POOR',      tip: 'Avg RSSI < −75 dBm — likely too far, low power, or interference' },
};

// Color the histogram bars by RSSI strength (greens = strong, reds = weak)
const rssiColor = (mid) => {
  if (mid >= -55) return '#10b981';
  if (mid >= -65) return '#22d3ee';
  if (mid >= -75) return '#f59e0b';
  return '#ef4444';
};

export default function SignalQuality() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(null);
  const [readers, setReaders] = useState([]);
  const [readerId, setReaderId] = useState(null);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      const a = ev.find(e => e.status === 'active') || ev[0];
      if (a) setEventId(a.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!eventId) return;
    getReaders({ event_id: eventId }).then(r => setReaders(r.data.data || [])).catch(() => {});
  }, [eventId]);

  const load = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    getRssiDistribution({ event_id: eventId, reader_id: readerId || undefined })
      .then(r => { setData(r.data.data || []); setSummary(r.data.summary || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventId, readerId]);

  useEffect(() => { load(); }, [load]);

  const q = QUALITY[summary.quality] || QUALITY.fair;
  const totalReads = data.reduce((s, r) => s + r.count, 0);

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: PRIMARY }}>
            <SignalFilled style={{ marginRight: 8, color: TEAL }} />Signal Quality / RSSI
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            UHF read-strength distribution — diagnose coverage, antenna placement and interference
          </Text>
        </Col>
        <Col>
          <Space>
            <Select value={eventId} onChange={setEventId} style={{ width: 200 }} placeholder="Event">
              {events.map(e => (
                <Option key={e.id} value={e.id}>
                  <Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} />
                </Option>
              ))}
            </Select>
            <Select value={readerId} onChange={setReaderId} placeholder="All readers" allowClear style={{ width: 220 }}>
              {readers.map(r => <Option key={r.id} value={r.id}><ApiOutlined /> {r.reader_name}</Option>)}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {/* Summary KPIs */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `4px solid ${q.color}` }}>
            <Statistic
              title="Quality"
              value={q.label}
              valueStyle={{ color: q.color, fontSize: 22, fontWeight: 700, letterSpacing: 1 }}
            />
            <Text type="secondary" style={{ fontSize: 10 }}>{q.tip}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${TEAL}` }}>
            <Statistic title="Average RSSI" value={summary.avg_rssi != null ? `${summary.avg_rssi}` : '—'} suffix="dBm" valueStyle={{ color: TEAL }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #f59e0b' }}>
            <Statistic title="Range" value={`${summary.min_rssi || '—'} → ${summary.max_rssi || '—'}`} valueStyle={{ fontSize: 18, color: '#f59e0b' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${PRIMARY}` }}>
            <Statistic title="Sample Reads" value={(summary.total_reads || 0).toLocaleString()} valueStyle={{ color: PRIMARY }} />
          </Card>
        </Col>
      </Row>

      {/* Quality alert */}
      {summary.quality && summary.quality !== 'excellent' && summary.quality !== 'good' && (
        <Alert
          type={summary.quality === 'poor' ? 'error' : 'warning'}
          showIcon
          style={{ marginBottom: 16, borderRadius: 8 }}
          message={`Signal quality is ${q.label.toLowerCase()}`}
          description={
            <span>
              Average RSSI of <strong>{summary.avg_rssi} dBm</strong> indicates {summary.quality === 'poor' ? 'poor read conditions' : 'sub-optimal placement'}.
              {' '}Consider checking antenna power, repositioning readers closer to badge height (1.2–1.6 m), or reducing nearby metal/interference.
            </span>
          }
        />
      )}

      {/* RSSI Histogram */}
      <Card title="RSSI Distribution Histogram" style={{ borderRadius: 10, marginBottom: 16 }}>
        {data.length === 0 ? <Empty description="No reads recorded" /> : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data} margin={{ top: 24, right: 24, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="bucket" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} />
              <RTooltip
                formatter={(v) => `${v.toLocaleString()} reads (${Math.round(v / totalReads * 100)}%)`}
                labelFormatter={(l) => `RSSI ${l} dBm`}
              />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {data.map((entry, i) => <Cell key={i} fill={rssiColor(entry.midpoint)} />)}
                <LabelList dataKey="count" position="top" formatter={v => v > totalReads * 0.04 ? v.toLocaleString() : ''} style={{ fontSize: 10, fill: '#64748b' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}

        {/* Legend */}
        <Divider style={{ margin: '12px 0' }} />
        <Space wrap size={20}>
          <Text type="secondary" style={{ fontSize: 11 }}>Strength scale:</Text>
          {[
            { l: '≥ −55 dBm  Excellent', c: '#10b981' },
            { l: '−65 to −55 Good',     c: '#22d3ee' },
            { l: '−75 to −65 Fair',     c: '#f59e0b' },
            { l: '< −75 dBm  Poor',     c: '#ef4444' },
          ].map(s => (
            <Space key={s.l} size={6}>
              <span style={{ width: 14, height: 14, borderRadius: 4, background: s.c, display: 'inline-block' }}/>
              <Text style={{ fontSize: 11 }}>{s.l}</Text>
            </Space>
          ))}
        </Space>
      </Card>

      {/* Engineering tips */}
      <Card title="Quick Diagnostic Tips" style={{ borderRadius: 10 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={8}>
            <Card size="small" style={{ borderTop: '3px solid #10b981', height: '100%' }}>
              <Text strong>📈 Reads peak at high RSSI (≥ −55)</Text>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b' }}>
                Antenna is too close to walking path. Lower power or move antenna further. May cause excessive duplicate reads.
              </p>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" style={{ borderTop: '3px solid #f59e0b', height: '100%' }}>
              <Text strong>📊 Centred around −65 to −70</Text>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b' }}>
                Healthy distribution. Consistent gate-style reads at typical badge-to-antenna distance (1.5–3 m).
              </p>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" style={{ borderTop: '3px solid #ef4444', height: '100%' }}>
              <Text strong>📉 Long tail at low RSSI (≤ −80)</Text>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b' }}>
                Distant or fringe reads — likely tags from neighbouring zones. Add an RSSI floor filter (e.g., reject below −78).
              </p>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
}
